import {
  IUpdateRawQueryListApiModel,
  IUpdateRawQueryList,
} from '@models/Update.model';
import { InfiniteData } from 'react-query';
import { IPaginationResult } from '@models/Software.model';
import { differenceInDays } from 'date-fns';
import { INewUpdates } from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';

const UPDATES_OPTIMISTIC_DAYS = 1;
/**
 * Check if update from matview is fresh
 */
export function isUpdateNew(
  foundInResult: IUpdateRawQueryList,
  optimisticUpdate: IUpdateRawQueryListApiModel,
) {
  const optimisticUpdatedAt = new Date(optimisticUpdate.updatedAt);
  return foundInResult.updatedAt >= optimisticUpdatedAt;
}

export function findUpdatedUpdatesToRemove(
  newUpdates: IUpdateRawQueryList[],
  updatedUpdates: INewUpdates[],
) {
  // more recently updated
  const updatedUpdatesToRemove = newUpdates.filter(result => {
    // same id.
    const sameId = updatedUpdates.find(update => {
      return update.update.id === result.id;
    });
    if (!sameId) return false;
    // and more recently updated or same updateat
    const currentOrNewer = isUpdateNew(result, sameId.update);
    return currentOrNewer;
  });

  // or older than a day
  const olderThanADay = updatedUpdates.filter(update => {
    const now = Date.now();
    const isOlderThanADay =
      differenceInDays(now, new Date(update.date)) >= UPDATES_OPTIMISTIC_DAYS;
    return isOlderThanADay;
  });

  const oldUpdateIds = olderThanADay.map(update => update.update.id);
  const updateIds = updatedUpdatesToRemove.map(update => update.id);
  const allUpdateIds = [...updateIds, ...oldUpdateIds];
  return allUpdateIds;
}
export function findNewUpdatesToRemove(
  newUpdates: IUpdateRawQueryList[],
  newOptimisticUpdates: INewUpdates[],
) {
  const updatesToRemove = newUpdates.filter(result => {
    // same id.
    const sameId = newOptimisticUpdates.find(update => {
      return update.update.id === result.id;
    });
    // Remove only if there's an update of same id in the list
    return !!sameId;
  });
  // or old ones
  const now = Date.now();
  const oldUpdates = newOptimisticUpdates.filter(update => {
    return (
      differenceInDays(now, new Date(update.date)) >= UPDATES_OPTIMISTIC_DAYS
    );
  });

  const updateIds = updatesToRemove.map(update => update.id);
  const oldUpdateIds = oldUpdates.map(update => update.update.id);
  const allUpdateIds = [...updateIds, ...oldUpdateIds];
  return allUpdateIds;
}
export function toNewFormat(
  newData: InfiniteData<IPaginationResult<IUpdateRawQueryList>> | undefined,
) {
  const newDataArr = newData?.pages ?? [];
  const results = newDataArr
    .map(pageData => pageData.results)
    .reduce(function reduce(prev, next) {
      return prev.concat(next);
    }, []);
  const { hasMore } = newDataArr.at(newDataArr.length - 1) || {};
  // if new, add to list optimistic
  return { hasMore: !!hasMore, results };
}
